/*************************************************************
 ************************** FUNCTIONS *************************
 *************************************************************/

/**
 * watchAwaitSelector
 * * Uses mutation observer to watch for 'selector' involved in DOM changes (added, removed, class change, content change, etc)
 * @param {Function} callback A callback function to be run whenever DOM changes are detected, can be anonymous or named
 * @param {String} selector Element(s) to look for involved in DOM changes, you can watch multiple selectors by separating via comma '.class1, .class2, #id3'
 * @param {Node} rootNode Node to watch from, typically document
 * @param {Number} fallbackDelay Interval to be used if the browser does not support MutationObserver, value is in milliseconds (2000 = 2 seconds)
 */
export const awaitSelector = (selector, rootNode, fallbackDelay) =>
  new Promise((resolve, reject) => {
    try {
      const root = rootNode || document;
      const ObserverClass = MutationObserver || WebKitMutationObserver || null;
      const mutationObserverSupported = typeof ObserverClass === "function";
      let observer;
      const stopWatching = () => {
        if (observer) {
          if (mutationObserverSupported) {
            observer.disconnect();
          } else {
            clearInterval(observer);
          }
          observer = null;
        }
      };
      const findAndResolveElements = () => {
        const allElements = root.querySelectorAll(selector);
        if (allElements.length === 0) return;
        const newElements = [];
        const attributeForBypassing = "data-awaitselector-resolved";
        allElements.forEach((el, i) => {
          if (typeof el[attributeForBypassing] === "undefined") {
            allElements[i][attributeForBypassing] = "";
            newElements.push(allElements[i]);
          }
        });
        if (newElements.length > 0) {
          stopWatching();
          resolve(newElements);
        }
      };
      if (mutationObserverSupported) {
        observer = new ObserverClass((mutationRecords) => {
          const nodesWereAdded = mutationRecords.reduce(
            (found, record) =>
              found || (record.addedNodes && record.addedNodes.length > 0),
            false
          );
          if (nodesWereAdded) {
            findAndResolveElements();
          }
        });
        observer.observe(root, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeOldValue: true,
          characterData: true,
          characterDataOldValue: true
        });
      } else {
        observer = setInterval(findAndResolveElements, fallbackDelay || 250);
      }
      findAndResolveElements();
    } catch (exception) {
      reject(exception);
    }
  });

export const watchAwaitSelector = (callback, selector, rootNode, fallbackDelay) => {
  (function awaiter(continueWatching = true) {
    if (continueWatching === false) return;
    awaitSelector(selector, rootNode, fallbackDelay)
      .then(callback)
      .then(awaiter);
  })();
};



// If selector is an ID, include the #, and if it is a class, include the .
export function addAttribute(selector, attribute, attributeValue) {
  // querySelectorAll creates an array of the matches
  var e = document.querySelectorAll(selector);
  if (e.length > 0) {
    for (var i = 0; i < e.length; i++) {
      e[i].setAttribute(attribute, attributeValue);
    }
  }
}

// If selector is an ID, include the #, and if it is a class, include the .
export function delAttribute(selector, attribute) {
  // querySelectorAll creates an array of the matches
  var e = document.querySelectorAll(selector);
  // For each item in the array
  for (var i = 0; i < e.length; i++) {
    // Remove the desired attribute
    e[i].removeAttribute(attribute);
  }
}

// Create an aria-label that contains the innerHTML of an element
// Optionally add text before and/or after the content
// If selector is an ID, include the #, and if it is a class, include the .
export function ariaLabelFromInnerHTML(
  textBefore,
  selectorToAddLabel,
  textSourceSelector,
  textAfter
) {
  // If fourth parameter isn't passed, make it an empty string instead of undefined
  textAfter === undefined ? (textAfter = "") : textAfter;
  // Array of elements to set the aria-label for
  var addLabelTo = document.querySelectorAll(selectorToAddLabel);
  // Array of elements with text to use as aria-label
  var addTextFrom = document.querySelectorAll(textSourceSelector);
  // For each item in the array
  for (let i = addLabelTo.length - 1; i >= 0; i--) {
    // Remove any leading/trailing whitespace
    addTextFrom[i].innerHTML = addTextFrom[i].innerHTML.trim();
    // Set aria-label
    addLabelTo[i].setAttribute(
      "aria-label",
      textBefore + addTextFrom[i].innerHTML + textAfter
    );
  }
}

// Wrap element in a new element
// example: wrapping an anchor with class "wrap_me" into a new div element
// wrap(document.querySelector('a.wrap_me'), document.createElement('div'));
export function wrap(el, wrapper) {
  el.parentNode.insertBefore(wrapper, el);
  wrapper.appendChild(el);
}

// Every interval of 'time' check if 'selector' exists
// If 'selector' exists, 'runCode' and stop the timer
// Check 'maxTries' number of times before exiting (set to '-1' to have no limit)
export function wait(selector, time, tries, maxTries, runCode) {
  // If tries wasn't defined for some reason, set it to 0
  typeof tries === "undefined" ? (tries = 0) : (tries = tries);
  // Creates a true array from any matching elements
  var selectors = document.querySelectorAll(selector);
  // If at least one matching selector is found
  if (selectors.length > 0) {
    for (var i = 0; i < selectors.length; i++) {
      // Run passed code
      runCode();
    }
  }
  // If no matches were found
  else {
    // Wait 'time' then run code below
    setTimeout(function() {
      // Always restart the timer
      if (maxTries === -1) {
        // Call the timer again
        wait(selector, time, tries, maxTries, runCode);
      }
      // If more tries allowed
      else if (maxTries > tries) {
        // Increment number of tries
        tries++;
        // Start the timer again, passing new 'tries' value
        // This is recommended over using a global variable
        // outside of the 'wait' function to keep count
        wait(selector, time, tries, maxTries, runCode);
      }
      // If checked maximum number of times
      else {
      }
    }, time);
  }
}


export function setUpClickAndEnterKey(element, restoreFocusToElement = null) {
  // Make click event
  let clickClose = new CustomEvent('click', { bubbles: true, cancelable: true });

  element.addEventListener('keypress', function(e) {
    // If close button is focused, and Enter key is pressed...
    if(e.key === 'Enter') {
      // Click the button (triggering Elementor modal close)
      element.dispatchEvent(clickClose);

      if(restoreFocusToElement !== null) {
        restoreFocusToElement.setAttribute("tabindex", "0");
        restoreFocusToElement.focus();
      }
    }
  });
}

export function restoreFocus(element) {
  element.setAttribute("tabindex", "0");
  element.focus();
}

export function trapFocus(element) {
    element.focus();
    var focusableEls = element.querySelectorAll('a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled]),  .dialog-close-button, .uael-modal-close, [tabindex]');
        var firstFocusableEl = focusableEls[0];
        var lastFocusableEl = focusableEls[focusableEls.length - 1];
        var KEYCODE_TAB = 9;

    element.addEventListener('keydown', function(e) {
        var isTabPressed = (e.key === 'Tab' || e.keyCode === KEYCODE_TAB);

        if (!isTabPressed) {
            return;
        }

        if ( e.shiftKey ) /* shift + tab */ {
            if (document.activeElement === firstFocusableEl) {
                lastFocusableEl.focus();
                e.preventDefault();
            }
        } else /* tab */ {
            if (document.activeElement === lastFocusableEl) {
                firstFocusableEl.focus();
                e.preventDefault();
            }
        }

    });
}

export function replaceElement(element, newElementType, classes, url) {
  let newElement = document.createElement(newElementType);

  // If link element, add URL if it exists
  if (newElementType === "a" && url !== undefined) {
    newElement.href = url;
  }

  // Add any classes from original to new element
  if(element !== null && element !== undefined) {
    let classNames = element.className.trim().split(" ");
    if (classNames != "" && classNames.length > 0) {
      newElement.classList.add(classNames);
    }
  }

  // Add any additional classes
  if (classes !== undefined && typeof classes === "object") {
    // In case you need to customize/fix output with custom class
    newElement.classList.add(...classes);
  }

  // Wrap the element with the new element
  wrap(element, newElement);

  // Replace the element with its content
  element.outerHTML = element.innerHTML;
}